/**
 * Per-module walkthrough config for the Feature Explorer.
 *
 * Each entry keys off the module title in FeatureExplorer.jsx MODULES[].t.
 * A module can have EITHER:
 *   - `previewUrl` — YouTube/Vimeo/Loom embed URL (rendered in an <iframe>) OR
 *                   a direct .mp4/.webm URL (rendered in a <video>).
 *   - `tourSteps`  — a scripted step-by-step tour rendered by DemoTourModal.
 *                   Each step: { title, body, image?, cta? }.
 *
 * When neither is set the modal shows the friendly "coming soon" placeholder.
 * To wire a real Loom recording later, just add `previewUrl` here — no code
 * changes needed.
 */
export const WALKTHROUGH_CONFIG = {
  "Ask TotalReach": {
    tourSteps: [
      {
        title: "Meet your AI Chief of Staff",
        body: "Ask TotalReach sits inside every screen and reads your live campaign — spend pacing, opponent ads, pending approvals, calendar and fundraising. Ask it anything and it acts.",
      },
      {
        title: "One box, real answers",
        body: "Type in plain English: \"How should I spend the last $12k this week?\" It looks at your race, budget and cost-per-visit data and returns a channel-by-channel recommendation.",
      },
      {
        title: "It doesn't just talk — it does",
        body: "Approve a pending creative, schedule the daily brief, create a canvassing event or refund a donation — all from the chat. Every action is logged to the audit trail.",
      },
      {
        title: "Export as a briefing PDF",
        body: "One click turns the conversation into a branded briefing for the candidate — perfect for morning stand-ups or a nightly wrap.",
        cta: "Open Ask TotalReach",
      },
    ],
  },
  "AI Assistant": {
    tourSteps: [
      { title: "Describe your race", body: "Tell the AI who you are and who you're trying to reach — no forms, just a sentence." },
      { title: "Get a full plan", body: "Audience, geography, channel mix, budget breakdown and forecast — generated in under 30 seconds." },
      { title: "One-tap launch", body: "Approve once and every channel goes live with proper creative sizing, targeting and pacing." , cta: "Try AI Assistant" },
    ],
  },
  "CTV Campaigns": {
    tourSteps: [
      { title: "Household-precise TV", body: "Reach voters on Hulu, YouTube TV, Peacock, Paramount+ and 100+ streaming apps with the same targeting as your digital." },
      { title: "Voter-file targeting", body: "Pull an audience straight from your registered-voter file — no third-party data broker needed." },
      { title: "Real-time delivery", body: "Watch impressions, completes and unique households light up as your ad rolls out." , cta: "Create a CTV campaign" },
    ],
  },
  "Fundraising": {
    tourSteps: [
      { title: "Stripe-native donations", body: "Every plan ships with a Stripe-powered donation page — cards, ACH, Apple Pay and recurring gifts." },
      { title: "Compliance built in", body: "Contribution limits, employer/occupation collection and attestations enforced at checkout." },
      { title: "Refunds in one click", body: "Chargeback UI, donor receipts and export-ready reports mean no more messy spreadsheets." , cta: "Open Fundraising" },
    ],
  },
  "Text-to-Give": {
    tourSteps: [
      { title: "Publish a keyword", body: "Something like GIVE, JOIN or your candidate's last name — texters get a personalized donation link back in seconds." },
      { title: "Twilio-powered", body: "Bring your own Twilio number for full compliance and cost transparency." },
      { title: "Attribution baked in", body: "Every SMS-driven donation shows up in Source Economics so you know which keyword is working." , cta: "Set up Text-to-Give" },
    ],
  },
  // Everything else uses the friendly "coming soon" placeholder until we add
  // a real recording. To wire one, just add `previewUrl: "https://youtube.com/embed/..."` or a scripted tour.
};
